/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChannelReference } from '../models/ChannelReference';
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { HrppAmplitude } from '../models/HrppAmplitude';
import type { HrppPattern } from '../models/HrppPattern';
import type { HrppPlaybackRate } from '../models/HrppPlaybackRate';
import type { HrppState } from '../models/HrppState';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class HrppService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get the current HRPP state of the device.
     * Get the current HRPP state of the device.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hrpp">Handy Repeating Pattern Protocol (HRPP)</a> for additional information about the HRPP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HRPP state.
     * @throws ApiError
     */
    public getHrppState(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HrppState;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hrpp/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Start the HRPP playback.
     * Start the HRPP playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hrpp">Handy Repeating Pattern Protocol (HRPP)</a> for additional information about the HRPP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HRPP state.
     * @throws ApiError
     */
    public startHrpp(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HrppState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hrpp/start',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Stop the HRPP playback.
     * Stop the HRPP playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hrpp">Handy Repeating Pattern Protocol (HRPP)</a> for additional information about the HRPP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device response.
     * @throws ApiError
     */
    public stopHrpp(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<{
        result?: HrppState;
    }> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hrpp/stop',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Set the HRPP playback-rate.
     * Set the HRPP playback-rate.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hrpp">Handy Repeating Pattern Protocol (HRPP)</a> for additional information about the HRPP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody HRPP playback-rate
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HRPP state.
     * @throws ApiError
     */
    public setHrppPlaybackRate(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            playback_rate: HrppPlaybackRate;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HrppState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hrpp/playbackrate',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Set the HRPP amplitude.
     * Set the HRPP amplitude.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hrpp">Handy Repeating Pattern Protocol (HRPP)</a> for additional information about the HRPP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody HRPP amplitude
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HRPP state.
     * @throws ApiError
     */
    public setHrppAmplitude(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            amplitude: HrppAmplitude;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HrppState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hrpp/amplitude',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get the available HRPP patterns on the device.
     * Get the available HRPP patterns on the device.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hrpp">Handy Repeating Pattern Protocol (HRPP)</a> for additional information about the HRPP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Available HRPP patterns.
     * @throws ApiError
     */
    public getHrppPatterns(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        /**
         * The available HRPP patterns.
         */
        result?: Array<HrppPattern>;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hrpp/patterns',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Activate the pattern at the specified slot.
     * Activates the pattern at the specified slot. The pattern list with slot position can be obtained from the GET /hrpp/patterns endpoint.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hrpp">Handy Repeating Pattern Protocol (HRPP)</a> for additional information about the HRPP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param slotNo Activates the pattern at the specified slot. The pattern list with slot position can be obtained from the GET /hrpp/patterns endpoint.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HRPP state.
     * @throws ApiError
     */
    public activateHrppPattern(
        xConnectionKey: (ConnectionKey | ChannelReference),
        slotNo: number,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HrppState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hrpp/patterns/{slotNo}/activate',
            path: {
                'slotNo': slotNo,
            },
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

}
